/**
 * Build identity for the running bundle.
 *
 * The question this answers is "which code is the browser in front of me
 * actually running", asked while something is already wrong. CloudFront serves a
 * hashed bundle behind a cached index.html, so a deploy that has finished is not
 * the same claim as a visitor having received it — the only trustworthy answer
 * comes from the bundle itself, which is why this is baked in at build time
 * rather than fetched.
 */

/** What vite.config.js injects when it cannot determine the commit. */
export const UNKNOWN_COMMIT = 'unknown'

/**
 * Renders the build identity as the single line the footer shows.
 *
 * Both halves earn their place and neither implies the other: the commit says
 * what was built, and the date distinguishes one deploy of that commit from a
 * later redeploy of the same one. Kept to a short, copy-pasteable string —
 * someone reads this out to support, or pastes it into a ticket.
 *
 * "Build" rather than "Version", deliberately. A version number is ordered, and
 * a commit SHA is not: labelling one as a version invites "is 200c62a newer than
 * 1f0de4c?", which has no answer without a git checkout, in exactly the incident
 * conversation this line exists to shorten. The date carries the ordering
 * instead. Revisit only if CWA gains real releases to number.
 * @param commit Short commit SHA the bundle was built from.
 * @param builtAt UTC build date as ISO `YYYY-MM-DD`.
 * @returns Footer text, e.g. `Build a1b2c3d · 2026-09-17`.
 */
export function formatBuildLabel(commit: string, builtAt: string): string {
  // Empty rather than absent is the realistic failure here: `define` always
  // substitutes something, and a git call that returned a blank line would
  // otherwise render as "Build  · 2026-09-17" and read as a rendering bug
  // rather than as missing provenance.
  return `Build ${[commit || UNKNOWN_COMMIT, builtAt].filter(Boolean).join(' · ')}`
}

/** The build label for this bundle, resolved once at module load. */
export const BUILD_LABEL = formatBuildLabel(__CWA_COMMIT__, __CWA_BUILT_AT__)
